import type { AdminBaseUser, AdminOrganizationsResponse, SuperuserParseValidateResponse } from '@/client/types.gen'
import {
  executeBatchUploadSuperuserBatchUploadsExecutePostMutation,
  getAllOrganizationsSuperuserOrganizationsGetOptions,
  parseValidateSuperuserBatchUploadsParseValidatePostMutation,
} from '@/client/@tanstack/react-query.gen'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, CheckCircle2, Loader2, Upload } from 'lucide-react'
import { useMemo, useState } from 'react'

export const Route = createFileRoute('/_authed/admin/bulk-upload')({
  component: BulkUploadPage,
})

type Step = 'configure' | 'preview' | 'execute'

function BulkUploadPage() {
  const [step, setStep] = useState<Step>('configure')
  const [selectedOrgId, setSelectedOrgId] = useState<string>('')
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [file, setFile] = useState<File>()
  const [previewData, setPreviewData] = useState<SuperuserParseValidateResponse>()

  const { toast } = useToast()

  const { data: orgsData, isLoading: orgsLoading } = useQuery({
    ...getAllOrganizationsSuperuserOrganizationsGetOptions(),
  })

  const usersForOrg = useMemo(() => {
    if (!orgsData || !selectedOrgId)
      return []
    return orgsData.usersByOrg[selectedOrgId] ?? []
  }, [orgsData, selectedOrgId])

  const parseValidateMutation = useMutation({
    ...parseValidateSuperuserBatchUploadsParseValidatePostMutation({
      // Override file validation: generated zod schema treats file as z.string()
      // but browser needs File objects for actual file uploads
      requestValidator: async data => data,
    }),
    onError: (error) => {
      toast({
        title: 'Validation failed',
        description: String(error),
        variant: 'destructive',
      })
    },
  })

  const executeMutation = useMutation({
    ...executeBatchUploadSuperuserBatchUploadsExecutePostMutation({
      // Bypass request validation: parse-validate response includes extra fields
      // (e.g. websiteDomain) that the execute request zod schema rejects
      requestValidator: async data => data,
    }),
    onError: (error) => {
      toast({
        title: 'Upload failed',
        description: String(error),
        variant: 'destructive',
      })
    },
  })

  const handleParseValidate = () => {
    if (!file || !selectedOrgId || !selectedUserId)
      return
    parseValidateMutation.mutate(
      {
        body: {
          organization_id: selectedOrgId,
          owner_user_id: selectedUserId,
          file,
        },
      },
      {
        onSuccess: (data) => {
          setPreviewData(data)
          setStep('preview')
        },
      },
    )
  }

  const handleExecute = () => {
    if (!previewData)
      return
    executeMutation.mutate(
      {
        body: {
          organizationId: selectedOrgId,
          ownerUserId: selectedUserId,
          newLeads: previewData.newLeads,
          matchesWithExistingCompanies: previewData.matchesWithExistingCompanies,
          duplicateWithExistingAccounts: previewData.duplicateWithExistingAccounts,
          batchUploadData: previewData.batchUploadData,
          leadSource: previewData.leadSource,
          validationToken: previewData.validationToken,
        },
      },
      {
        onSuccess: (data) => {
          setStep('execute')
          toast({
            title: 'Upload successful',
            description: data.message,
          })
        },
      },
    )
  }

  const selectedOrg = orgsData?.organizations.find(o => o.id === selectedOrgId)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Bulk Upload</h1>
        <p className="text-muted-foreground">Upload accounts into any organization on behalf of a user</p>
      </div>

      {step === 'configure' && (
        <ConfigureStep
          orgsData={orgsData}
          orgsLoading={orgsLoading}
          selectedOrgId={selectedOrgId}
          selectedUserId={selectedUserId}
          usersForOrg={usersForOrg}
          file={file}
          onOrgChange={(orgId) => {
            setSelectedOrgId(orgId)
            setSelectedUserId('')
          }}
          onUserChange={setSelectedUserId}
          onFileChange={setFile}
          onSubmit={handleParseValidate}
          isSubmitting={parseValidateMutation.isPending}
        />
      )}

      {(step === 'preview' || (step === 'execute' && !executeMutation.data)) && previewData && (
        <PreviewStep
          data={previewData}
          orgName={selectedOrg?.name ?? selectedOrgId}
          onBack={() => setStep('configure')}
          onExecute={handleExecute}
          isExecuting={executeMutation.isPending}
        />
      )}

      {step === 'execute' && executeMutation.data && (
        <ExecuteResult
          data={executeMutation.data}
          onReset={() => {
            setStep('configure')
            setFile(undefined)
            setPreviewData(undefined)
            setSelectedOrgId('')
            setSelectedUserId('')
            executeMutation.reset()
            parseValidateMutation.reset()
          }}
        />
      )}
    </div>
  )
}

function ConfigureStep({
  orgsData,
  orgsLoading,
  selectedOrgId,
  selectedUserId,
  usersForOrg,
  file,
  onOrgChange,
  onUserChange,
  onFileChange,
  onSubmit,
  isSubmitting,
}: {
  orgsData: AdminOrganizationsResponse | undefined
  orgsLoading: boolean
  selectedOrgId: string
  selectedUserId: string
  usersForOrg: Array<AdminBaseUser>
  file: File | undefined
  onOrgChange: (orgId: string) => void
  onUserChange: (userId: string) => void
  onFileChange: (file: File | undefined) => void
  onSubmit: () => void
  isSubmitting: boolean
}) {
  const canSubmit = selectedOrgId && selectedUserId && file && !isSubmitting

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configuration</CardTitle>
        <CardDescription>Select the target organization, owner user, and upload file</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="org-select">Organization</Label>
          {orgsLoading
            ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />
                  Loading organizations...
                </div>
              )
            : (
                <Select value={selectedOrgId} onValueChange={onOrgChange}>
                  <SelectTrigger id="org-select">
                    <SelectValue placeholder="Select an organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgsData?.organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="user-select">Account Owner</Label>
          <Select
            value={selectedUserId}
            onValueChange={onUserChange}
            disabled={!selectedOrgId}
          >
            <SelectTrigger id="user-select">
              <SelectValue placeholder={selectedOrgId ? 'Select a user' : 'Select an organization first'} />
            </SelectTrigger>
            <SelectContent>
              {usersForOrg.map(user => (
                <SelectItem key={user.id} value={user.id}>
                  {user.first_name && user.last_name
                    ? `${user.first_name} ${user.last_name} (${user.email})`
                    : user.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="file-upload">CSV or Excel File</Label>
          <Input
            id="file-upload"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => {
              onFileChange(e.target.files?.[0])
            }}
          />
          <p className="text-xs text-muted-foreground">
            Required columns: name, website_url, address
          </p>
        </div>

        <Button
          onClick={onSubmit}
          disabled={!canSubmit}
          className="w-full"
        >
          {isSubmitting
            ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Validating...
                </>
              )
            : (
                <>
                  <Upload className="mr-2 size-4" />
                  Parse & Validate
                </>
              )}
        </Button>
      </CardContent>
    </Card>
  )
}

function PreviewStep({
  data,
  orgName,
  onBack,
  onExecute,
  isExecuting,
}: {
  data: SuperuserParseValidateResponse
  orgName: string
  onBack: () => void
  onExecute: () => void
  isExecuting: boolean
}) {
  const totalValid = data.newLeads.length + data.matchesWithExistingCompanies.length
  const totalInvalid = data.invalidRecords.length
  const totalDuplicates = data.duplicatesWithinBatch.length + data.duplicateWithExistingAccounts.length

  return (
    <div className="space-y-4">
      <Button variant="ghost" onClick={onBack} size="sm">
        <ArrowLeft className="mr-2 size-4" />
        Back to configuration
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
          <CardDescription>
            Uploading to
            {' '}
            <strong>{orgName}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-lg border p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{totalValid}</div>
              <div className="text-xs text-muted-foreground">New Accounts</div>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <div className="text-2xl font-bold">{data.matchesWithExistingCompanies.length}</div>
              <div className="text-xs text-muted-foreground">Existing Companies</div>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <div className="text-2xl font-bold text-yellow-600">{totalDuplicates}</div>
              <div className="text-xs text-muted-foreground">Duplicates</div>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <div className="text-2xl font-bold text-red-600">{totalInvalid}</div>
              <div className="text-xs text-muted-foreground">Invalid</div>
            </div>
          </div>

          {data.warningRecords.length > 0 && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900 dark:bg-yellow-950">
              <div className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                {data.warningRecords.length}
                {' '}
                warning(s)
              </div>
            </div>
          )}

          {data.newLeads.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-medium">New Accounts (first 20)</h3>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Website</TableHead>
                      <TableHead>Address</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.newLeads.slice(0, 20).map(lead => (
                      <TableRow key={lead.rowIndex}>
                        <TableCell className="font-medium">{lead.name}</TableCell>
                        <TableCell className="text-muted-foreground">{lead.websiteDomain}</TableCell>
                        <TableCell className="text-muted-foreground">{lead.address}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {data.newLeads.length > 20 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  ...and
                  {' '}
                  {data.newLeads.length - 20}
                  {' '}
                  more
                </p>
              )}
            </div>
          )}

          {data.invalidRecords.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-medium text-red-600">Invalid Records</h3>
              <div className="rounded-md border border-red-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Errors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.invalidRecords.map(record => (
                      <TableRow key={record.rowIndex}>
                        <TableCell>{record.rowIndex + 1}</TableCell>
                        <TableCell>
                          <Badge variant="destructive">
                            {record.error}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <Button
            onClick={onExecute}
            disabled={totalValid === 0 || isExecuting}
            className="w-full"
          >
            {isExecuting
              ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Creating accounts...
                  </>
                )
              : (
                  `Create ${totalValid} Account${totalValid === 1 ? '' : 's'}`
                )}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

function ExecuteResult({
  data,
  onReset,
}: {
  data: { stats: { newAccounts: number, newCompaniesCreated: number, existingCompaniesFound: number, companiesWithErrors: number }, message: string }
  onReset: () => void
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle2 className="size-5 text-green-600" />
          Upload Complete
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{data.message}</p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border p-3 text-center">
            <div className="text-2xl font-bold">{data.stats.newAccounts}</div>
            <div className="text-xs text-muted-foreground">Accounts Created</div>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <div className="text-2xl font-bold">{data.stats.newCompaniesCreated}</div>
            <div className="text-xs text-muted-foreground">New Companies</div>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <div className="text-2xl font-bold">{data.stats.existingCompaniesFound}</div>
            <div className="text-xs text-muted-foreground">Existing Companies</div>
          </div>
          {data.stats.companiesWithErrors > 0 && (
            <div className="rounded-lg border p-3 text-center">
              <div className="text-2xl font-bold text-red-600">{data.stats.companiesWithErrors}</div>
              <div className="text-xs text-muted-foreground">Errors</div>
            </div>
          )}
        </div>
        <Button variant="outline" onClick={onReset} className="w-full">
          Upload Another File
        </Button>
      </CardContent>
    </Card>
  )
}
